import { useCallback } from 'react'
import { useShallow } from 'zustand/react/shallow'
import {
  useCreateTemplateAttribute,
  useUpdateTemplateAttribute,
  useDeleteTemplateAttribute,
} from '@/services/queries/template_attributes'
import { listTemplateAttributesQuery } from '@/services/queries/template_attributes'
import deepEqual from 'deep-equal'
import type { TemplateAttribute } from '../api/client'
import type { TimeTravelEvent } from './timeTravelStore'
import { useTimeTravelStore } from './timeTravelStore'

export const useTimeTravel = () => {
  const { mutateAsync: updateTemplateAttribute } = useUpdateTemplateAttribute({
    isTimeTraveling: true,
  })
  const { mutateAsync: createTemplateAttribute } = useCreateTemplateAttribute({
    isTimeTraveling: true,
  })
  const { mutateAsync: deleteTemplateAttribute } = useDeleteTemplateAttribute({
    isTimeTraveling: true,
  })

  const recordUndoEvent = useTimeTravelStore(useShallow((state) => state.undo))
  const recordRedoEvent = useTimeTravelStore(useShallow((state) => state.redo))
  const pastEvents = useTimeTravelStore((state) => state.pastEvents)
  const futureEvents = useTimeTravelStore((state) => state.futureEvents)

  const undo = useCallback(async () => {
    const pastEvent = pastEvents[pastEvents.length - 1]
    const canBeUndone = pastEvent && pastEvent.source === 'template_attribute'

    if (!canBeUndone) {
      return
    }

    const oldAttribute = pastEvent.old as TemplateAttribute | undefined
    const newAttribute = pastEvent.new as TemplateAttribute | undefined
    const overrides = {} as Partial<TimeTravelEvent>
    const documentPageId = (pastEvent?.new?.document_page ||
      pastEvent?.old?.document_page) as string
    const templateAttributes = await listTemplateAttributesQuery({
      documentPageId,
    })

    // find a template attribute that has the same attributes from the template attributes list
    const targetTemplateAttribute = templateAttributes.find(
      (t) =>
        t.data_type === pastEvent?.new?.data_type &&
        deepEqual(t.template_values, pastEvent?.new?.template_values || {}),
    )

    if (pastEvent.action === 'create' && newAttribute) {
      await deleteTemplateAttribute({
        templateAttributeId: targetTemplateAttribute?.id as string,
      })
    } else if (pastEvent.action === 'delete' && oldAttribute) {
      overrides.old = await createTemplateAttribute({
        documentPageId: oldAttribute?.document_page,
        payload: {
          data_type: oldAttribute?.data_type,
          template_values: oldAttribute?.template_values,
        },
      })
    } else if (pastEvent.action === 'update' && oldAttribute) {
      await updateTemplateAttribute({
        templateAttributeId: targetTemplateAttribute?.id as string,
        payload: {
          data_type: oldAttribute?.data_type,
          template_values: oldAttribute?.template_values,
        },
      })
    }

    recordUndoEvent(overrides)
  }, [
    createTemplateAttribute,
    deleteTemplateAttribute,
    pastEvents,
    recordUndoEvent,
    updateTemplateAttribute,
  ])

  const redo = useCallback(async () => {
    const futureEvent = futureEvents[futureEvents.length - 1]
    const canBeRedone =
      futureEvent && futureEvent.source === 'template_attribute'

    if (!canBeRedone) {
      return
    }

    const oldAttribute = futureEvent.old as TemplateAttribute | undefined
    const newAttribute = futureEvent.new as TemplateAttribute | undefined
    const overrides = {} as Partial<TimeTravelEvent>

    const documentPageId = (futureEvent?.new?.document_page ||
      futureEvent?.old?.document_page) as string
    const templateAttributes = await listTemplateAttributesQuery({
      documentPageId,
    })

    // find a template attribute that has the same attributes from the template attributes list
    const targetTemplateAttribute = templateAttributes.find(
      (t) =>
        t.data_type === futureEvent?.old?.data_type &&
        deepEqual(t.template_values, futureEvent?.old?.template_values || {}),
    )

    if (futureEvent.action === 'create' && newAttribute) {
      overrides.new = await createTemplateAttribute({
        documentPageId: newAttribute?.document_page,
        payload: {
          data_type: newAttribute?.data_type,
          template_values: newAttribute?.template_values,
        },
      })
    } else if (futureEvent.action === 'delete' && oldAttribute) {
      await deleteTemplateAttribute({
        templateAttributeId: targetTemplateAttribute?.id as string,
      })
    } else if (futureEvent.action === 'update' && newAttribute) {
      await updateTemplateAttribute({
        templateAttributeId: targetTemplateAttribute?.id as string,
        payload: {
          data_type: newAttribute?.data_type,
          template_values: newAttribute?.template_values,
        },
      })
    }

    recordRedoEvent(overrides)
  }, [
    createTemplateAttribute,
    deleteTemplateAttribute,
    futureEvents,
    recordRedoEvent,
    updateTemplateAttribute,
  ])

  return { undo, redo }
}
